/**
 * Striping Plan (STP) in the run form — pure helpers for the run modal and the payload
 * (spec docs/superpowers/specs/2026-10-14-striping-plan-gui-design.md, Part C).
 *
 * The modal asks POST /rsa/v2/striping-plan/check what each road will do (reuse earlier
 * PSD results or run PSD) and keeps three per-run settings per route. The run sends them
 * as `striping_data`; the backend re-checks every road at submit and wins.
 */
export const STP_TASK_LABEL = "Striping Plan (STP)";
export const PSD_TASK_LABEL = "Passing Sight Distance (PSD)";
// Settings key for the single-run form (multi-route runs key by route jsonId).
export const STRIPING_SINGLE_ROUTE_KEY = "__single__";

export const STRIPING_SETTING_FIELDS = [
  { key: "dis_before_intersection", label: "Window before intersection (ft)" },
  { key: "dis_after_intersection", label: "Window after intersection (ft)" },
  { key: "min_no_pass", label: "Shortest no-passing zone kept (ft)" },
];
// The service's defaults (Backend/Pipeline/utils/striping_plan.py FORM_SETTING_DEFAULTS).
export const STRIPING_SETTING_DEFAULTS = {
  dis_before_intersection: 200,
  dis_after_intersection: 100,
  min_no_pass: 50,
};
export const STRIPING_SETTING_MAX = 5280;

/** The problem with one typed setting, or null when it is a number from 0 to 5280. */
export const stripingSettingError = (raw) => {
  const text = String(raw ?? "").trim();
  const n = Number(text);
  if (text === "" || !Number.isFinite(n)) return "Enter a number of feet";
  if (n < 0) return "Must be 0 or more";
  if (n > STRIPING_SETTING_MAX) return `Must be at most ${STRIPING_SETTING_MAX}`;
  return null;
};

/**
 * `striping_data` for one run. Only settings that differ from the template's (`baseline`)
 * are sent, so a later template change still applies to values the user didn't touch.
 */
export const buildStripingData = ({
  values = {},
  baseline = STRIPING_SETTING_DEFAULTS,
  intersections = [],
} = {}) => {
  const settings = {};
  for (const { key } of STRIPING_SETTING_FIELDS) {
    const raw = (values || {})[key];
    if (raw == null || String(raw).trim() === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n !== Number((baseline || {})[key])) settings[key] = n;
  }
  return {
    settings,
    intersections: (intersections || []).map((p, i) => ({
      lat: p.lat,
      lng: p.lng,
      name: String(p.name ?? "").trim() || `Intersection ${i + 1}`,
    })),
  };
};

/** The run's `striping_data` when its tasks include Striping Plan, else null. */
export const stripingDataForTasks = (tasks, entry, intersections = []) =>
  (tasks || []).includes(STP_TASK_LABEL)
    ? buildStripingData({ values: entry?.values, baseline: entry?.baseline, intersections })
    : null;

const driveIds = (xmls) =>
  (xmls || [])
    .map((x) => String(x?.googleDriveId ?? "").trim())
    .filter(Boolean);

/** A multi-route run spec's (multiRouteRun.buildRunSpecs) STP and PSD lane file ids. */
export const stripingCheckFilesFromSpec = (spec) => {
  const byTask = spec?.xmlsByTask || {};
  return {
    stpFileIds: driveIds(byTask[STP_TASK_LABEL]),
    psdFileIds: byTask[PSD_TASK_LABEL] ? driveIds(byTask[PSD_TASK_LABEL]) : null,
  };
};

/**
 * The check endpoint's body, or null when there is nothing to check yet: by file ids
 * (multi-route: the route's lanes) or by folder (single run: Source/RSA_STP).
 */
export const buildStripingCheckRequest = ({
  projectId,
  stpTemplateId = null,
  stpFolderId = null,
  psdFolderId = null,
  stpFileIds = null,
  psdFileIds = null,
} = {}) => {
  if (projectId == null || projectId === "") return null;
  const base = {
    project_id: String(projectId),
    ...(stpTemplateId && { stp_template_id: stpTemplateId }),
  };
  if (Array.isArray(stpFileIds)) {
    if (stpFileIds.length === 0) return null;
    return {
      ...base,
      stp_file_ids: stpFileIds,
      ...(Array.isArray(psdFileIds) && psdFileIds.length > 0 && { psd_file_ids: psdFileIds }),
    };
  }
  if (!stpFolderId) return null;
  return {
    ...base,
    stp_folder_id: stpFolderId,
    ...(psdFolderId && { psd_folder_id: psdFolderId }),
  };
};

const directionText = (d) =>
  !d ? "—" : d.decision === "reuse" ? "Reuse PSD" : `PSD will run (${d.reason})`;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The modal's view of a check response. */
export const summarizeStripingCheck = (data) => {
  const roads = data?.roads || [];
  const rows = roads.map((r) => ({
    label: r.label,
    n: directionText(r.directions?.N),
    p: directionText(r.directions?.P),
  }));
  const running = roads.filter((r) =>
    Object.values(r.directions || {}).some((d) => d?.decision === "run"),
  ).length;
  let psdLine;
  if (running === 0) {
    psdLine = "Every road reuses earlier PSD results: no PSD runs for the striping plan.";
  } else if (data?.psd_selected) {
    psdLine = `PSD runs on ${plural(running, "road")} as part of this run's PSD.`;
  } else {
    const miles = Math.round(Number(data?.psd_miles_to_bill || 0) * 100) / 100;
    psdLine = `PSD will run on ${plural(running, "road")}: ${miles} mi billed as PSD.`;
  }
  const outside = data?.not_in_psd_selection || [];
  return {
    rows,
    skipped: (data?.skipped || []).map((s) => `${s.label}: ${s.reason} — skipped`),
    psdLine,
    blocking: outside.length
      ? `These roads need PSD but their lanes aren't in this run's PSD: ${outside.join(", ")}. Add them to PSD, or unselect PSD.`
      : null,
  };
};
